<script setup lang="ts">
import CheckboxToggle from "@/components/form/CheckboxToggle.vue"
import LoadingButton from "@/components/LoadingButton.vue"
import VarsityContentProcessor from "@/components/VarsityContentProcessor.vue"
import type { ChampionshipEventType } from "@/model/championship/championship-event-type"
import type { ChampionshipRegistrationSelectTeamTask } from "@/model/championship/championship-registration-select-team-task"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, $date, useApp, useHttpClient, useHttpGet, useLoadingState } from "@/vf"
import { computed, ref, toRefs } from "vue"
import type { ChampionshipRegistrationAcceptEventInfoData } from "./championship-registration"

const props = defineProps<TasksProps<ChampionshipRegistrationSelectTeamTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const http = useHttpClient()
const checkboxToggled = ref(false)
const { apiLink } = useApp()

/*─────────────────────────────────────┐
│  fetching                            │
└─────────────────────────────────────*/
const { data } = await useHttpGet<ChampionshipRegistrationAcceptEventInfoData>(
    `/championship/registration/${task.value.organizationId}/accept-event-info/${task.value.id}`,
)

const championship = computed(() => data.value!.championship!)

/*─────────────────────────────────────┐
│  accept                              │
└─────────────────────────────────────*/
const submitting = useLoadingState()
function submitAccept() {
    submitting.while(async () => {
        await http.post(`/championship/registration/${task.value.organizationId}/accept-event-info/${task.value.id}`)
        emit("updated")
    })
}

const trans = defineTranslations({
    checkbox_text: {
        de: "Ich habe die Informationen gelesen und akzeptiere die Teilnahmebedingungen.",
        en: "I have read the information and accept the terms of participation.",
    },
    accept: {
        de: "Akzeptieren",
        en: "Accept",
    },
    registration_date: {
        de: "bis {date}",
        en: "until {date}",
    },
})
</script>
<template>
    <div class="event-card-show overflow-hidden mb-4">
        <div class="event-card-show__inner">
            <div class="row">
                <div class="col-xl-3 col-lg-6 col-12 order-1">
                    <h3 class="event-card-show__title">{{ $t("@championship:championship.eventInformation") }}</h3>
                    <p class="mb-0">
                        <b>{{ $t("@championship:championship.eventType") }}:</b>
                        {{
                            (championship.eventTypes as ChampionshipEventType[] | undefined)
                                ?.map(c => c.name)
                                .join(", ")
                        }}
                    </p>
                    <p>
                        <b>{{ $t("@championship:championship.bids") }}:</b>
                        {{ championship.bids?.map(c => c.name).join(", ") }}
                    </p>
                    <address>
                        {{ championship.brandName }}
                        <br />
                        {{ championship.brandStreet }} {{ championship.brandStreetNumber }}
                        <br />
                        {{ championship.brandZip }} {{ championship.brandCity }}
                    </address>
                    <b>{{ $t("@championship:championship.location") }}:</b>
                    <address>
                        {{ championship.location }}
                        <br />
                        {{ championship.locationStreet }} {{ championship.locationStreetNumber }}
                        <br />
                        {{ championship.locationZip }} {{ championship.locationCity }}
                    </address>
                </div>
                <div
                    class="col-xl-9 col-12 order-3 order-xl-2 d-none d-lg-block"
                    v-for="price of championship.prices"
                    :key="price.id"
                >
                    <div class="row">
                        <div class="col-lg-9">
                            <div class="event-card-show__registration d-none d-lg-block mb-4">
                                <h3 class="event-card-show__title mb-2">
                                    {{ $t("@championship:championship.registration") }}
                                </h3>
                                <time :datetime="championship.registrationUntil">
                                    {{ trans("registration_date", { date: $date(championship.registrationUntil) }) }}
                                </time>
                            </div>

                            <div v-if="championship.infoDocumentClientName" class="mb-4">
                                <h3 class="event-card-show__title mb-2">Downloads</h3>
                                <div>
                                    <a
                                        :href="`/championship/registration/${task.organizationId}/download-event-info/${task.id}`"
                                    >
                                        <i class="fas fa-file-download mr-1"></i>
                                        {{ championship.infoDocumentClientName }}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div class="col-lg-3">
                            <div v-if="championship.logoName">
                                <img
                                    :src="apiLink(`/media/${championship.logoName}`)"
                                    :alt="championship.logoName"
                                    class="img-fluid mb-3"
                                />
                            </div>
                        </div>
                    </div>

                    <h3 class="event-card-show__title">{{ $t("@championship:championship.priceInformation") }}</h3>
                    <table class="table event-card-show__table">
                        <tbody>
                            <tr>
                                <th>{{ price.name }}</th>
                                <th class="text-right">{{ $t("@championship:championship.prices.early_rate") }}</th>
                                <th class="text-right">
                                    {{ $t("@championship:championship.prices.normal_rate") }}
                                </th>
                            </tr>
                            <tr>
                                <td>{{ $t("@championship:championship.prices.active_participant") }}</td>
                                <td class="text-right">{{ $currency(price.priceEarlyActiveCents) }}</td>
                                <td class="text-right">{{ $currency(price.priceNormalActiveCents) }}</td>
                            </tr>
                            <tr>
                                <td>{{ $t("@championship:championship.prices.plus_category") }}</td>
                                <td class="text-right">{{ $currency(price.priceEarlyAdditionalCategoryCents) }}</td>
                                <td class="text-right">
                                    {{ $currency(price.priceNormalAdditionalCategoryCents) }}
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t("@championship:championship.prices.coach") }}</td>
                                <td class="text-right">{{ $currency(price.priceEarlyCoachCents) }}</td>
                                <td class="text-right">{{ $currency(price.priceNormalCoachCents) }}</td>
                            </tr>
                            <tr>
                                <td>{{ $t("@championship:championship.prices.escort") }}</td>
                                <td class="text-right">{{ $currency(price.priceEarlyEscortCents) }}</td>
                                <td class="text-right">{{ $currency(price.priceNormalEscortCents) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="row border-top border-light pt-3" v-if="championship.infoText">
                <div class="col-12">
                    <VarsityContentProcessor :content="championship.infoText" />
                </div>
            </div>
        </div>

        <div class="px-5 pt-3" v-if="props.task.state === 'open'">
            <CheckboxToggle v-model="checkboxToggled" class="mb-4">
                <VarsityContentProcessor :content="trans('checkbox_text')" />
            </CheckboxToggle>

            <div class="d-flex justify-content-end">
                <LoadingButton :loading="submitting" :disabled="!checkboxToggled" @click="submitAccept">
                    {{ trans("accept") }}
                </LoadingButton>
            </div>
        </div>
    </div>
</template>
